/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react'
import { useTranslations } from 'next-intl'
import NextLink from 'next/link'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Image,
  Flex,
  Button,
} from '@chakra-ui/react'
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table'
import { FACILITY_TABLE_PLACEHOLDER } from '@/constants'
import { Flag } from '@/components/Flag'
import { useColorModeValue } from '@chakra-ui/react'
import { getImages } from '@/utils/getImages'

export type AllFacilitiesDataTableProps<Data extends object> = {
  data: Data[]
  columns: ColumnDef<Data, any>[]
}

export function AllFacilitiesDataTable<Data extends object>({
  data,
  columns,
}: AllFacilitiesDataTableProps<Data>) {
  const t = useTranslations()
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'facilityAvailability', desc: false },
  ])
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  })

  return (
    <Table size="sm">
      <Thead bg={useColorModeValue('gray.220', 'black')}>
        {table.getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id} cursor={'pointer'}>
            {headerGroup.headers.map((header) => {
              return (
                <Th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  h={50}
                  textTransform={'none'}
                  fontSize={'md'}
                  fontWeight={'400'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}

                  <chakra.span pl="4">
                    {header.column.getIsSorted() ? (
                      header.column.getIsSorted() === 'desc' ? (
                        <TriangleDownIcon
                          color={'brand.600'}
                          aria-label="sorted descending"
                        />
                      ) : (
                        <TriangleUpIcon
                          color={'brand.600'}
                          aria-label="sorted ascending"
                        />
                      )
                    ) : null}
                  </chakra.span>
                </Th>
              )
            })}
          </Tr>
        ))}
      </Thead>
      <Tbody bg={useColorModeValue('white', 'gray.600')}>
        {table.getRowModel().rows.map((row) => (
          <Tr key={row.id} h={75}>
            {row.getVisibleCells().map((cell: any) => {
              return (
                <React.Fragment key={cell.id}>
                  {cell.column.id === 'facilitiesName' && (
                    <Td h={50} textTransform={'none'}>
                      <Flex
                        alignItems={'center'}
                        fontWeight={'600'}
                        fontSize={'md'}
                      >
                        <Image
                          src={
                            getImages(
                              cell.row.original?.facilitiesImagesCsvPrioritized,
                              FACILITY_TABLE_PLACEHOLDER
                            )?.mainImage
                          }
                          h={10}
                          rounded={'xl'}
                          alt={`${cell.getValue()}`}
                          mr={6}
                          fallbackSrc={FACILITY_TABLE_PLACEHOLDER}
                        />
                        <Flag
                          mr={2}
                          countryCode={cell.row.original.countryCode}
                        />
                        {`${cell.getValue()}`}
                      </Flex>
                    </Td>
                  )}
                  {cell.column.id === 'facilityAvailability' && (
                    <>
                      {cell.getValue() === 'available' && (
                        <Td
                          h={50}
                          textTransform={'none'}
                          color={'green.450'}
                          fontWeight={'600'}
                        >
                          {t('facilities-list.units-available')}
                        </Td>
                      )}
                      {cell.getValue() === 'fully_booked' && (
                        <Td
                          h={50}
                          textTransform={'none'}
                          color={'red.550'}
                          fontWeight={'600'}
                        >
                          {t('facilities-list.fully-booked')}
                        </Td>
                      )}
                      {cell.getValue() === 'coming_soon' && (
                        <Td
                          h={50}
                          textTransform={'none'}
                          color={'orange.450'}
                          fontWeight={'600'}
                        >
                          {t('facilities-list.coming-soon')}
                        </Td>
                      )}
                    </>
                  )}
                  {cell.column.id === 'facilitiesId' && (
                    <Td h={50} textTransform={'none'}>
                      <NextLink href={`facilities/${cell.getValue()}`} passHref>
                        <Button
                          mb={2}
                          w={'full'}
                          as={'a'}
                          variant="black"
                          fontWeight={400}
                        >
                          {t('facilities-list.view-farm')}
                        </Button>
                      </NextLink>
                    </Td>
                  )}
                </React.Fragment>
              )
            })}
          </Tr>
        ))}
      </Tbody>
    </Table>
  )
}
